<template>
    <card-normal>
        <template #sections>
            <card-section area="card-header">
                <text-decorator bold :size="FontSize.sm">
                    Kans
                </text-decorator>
            </card-section>
        </template>

        <flex-container :gap="Size.sm">
            <div v-if="!isUserOfAssignedCompany">
                <score-block
                    :score="props.lead.score"
                />
            </div>
            <input-group
                v-else
                v-model="currentScore"
            >
                <template #input>
                    <select-input
                        :collapse-offset="{ y: 0, x: -10 }"
                        :items="scores"
                        variant="inline"
                        arrow-variant="unfold"
                    >
                        <template #active>
                            <score-block
                                :score="currentScore"
                            />
                        </template>
                        <template #item="{ bindings }">
                            <score-block
                                :score="bindings.item"
                            />
                        </template>
                    </select-input>
                </template>
            </input-group>
            <transition name="fade">
                <flex-container
                    v-if="hasChanges"
                    :gap="Size.sm"
                    justify-content="flex-end"
                >
                    <button-outline @click="cancel">
                        Annuleren
                    </button-outline>
                    <button-block @click="changeScore">
                        Opslaan
                    </button-block>
                </flex-container>
            </transition>
        </flex-container>
    </card-normal>
</template>

<script setup lang="ts">
    import {
        CardNormal,
        CardSection,
        InputGroup,
        SelectInput,
        TextDecorator,
        ButtonBlock,
        ButtonOutline,
        FlexContainer,
        Size,
        FontSize,
    } from '@keyvue/one-ui';

    import ScoreBlock from '@components/Blocks/ScoreBlock.vue';
    import { Lead, Score } from '@keyvue/one-api-client';
    import { ref, type Ref, watch } from 'vue';
    import useLeadSecurity from '@composables/security/useLeadSecurity';
    import useNotify from '@composables/useNotify';
    import useEventBus from '@composables/useEventBus';
    import { Referral, ReferralAPI } from '@keyvue/one-api-client-legacy';

    interface Props {
        lead: Lead;
    }

    const props = defineProps<Props>();
    const { isUserOfAssignedCompany } = await useLeadSecurity(props.lead);
    const { notifySaving, notifySaved } = useNotify();
    const eventBus = useEventBus();

    const currentScore = ref(props.lead.score) as Ref<Score>;
    const scores = ref(Score.values);
    const hasChanges = ref(false);

    const cancel = () => {
        currentScore.value = props.lead.score;
        hasChanges.value = false;
    };

    const changeReferralField = async (callback: (referral: Referral) => Referral) => {
        const closeNotification = notifySaving(undefined, 0);
        const legacyReferral = await ReferralAPI.get(props.lead.id.value);
        await ReferralAPI.update(props.lead.id.value, callback(legacyReferral));
        notifySaved();
        if (closeNotification) {
            closeNotification();
        }
    };

    const changeScore = async () => {
        await changeReferralField((referral) => {
            const localReferral = referral;
            localReferral.score = currentScore.value.value;
            return localReferral;
        });

        eventBus.dispatch('resource-manipulated', 'Lead');

        hasChanges.value = false;
    };

    watch(currentScore, () => {
        hasChanges.value = currentScore.value.value !== props.lead.score.value;
    });

    watch(props, () => {
        currentScore.value = props.lead.score;
    });
</script>
